import React, { useEffect, useState } from 'react'
import StatusPill from './StatusPill'
import { supabase } from '../lib/supabase'

const EMPTY = {
  component_type: 'fork',
  brand: '',
  model: '',
  model_code: '',
  year_from: '',
  year_to: '',
  damper: '',
  spring_type: 'unknown',
  travel_options: '',
  adjusters: [
    { key: 'lsc', label: 'Low Speed Compression', clicks_total: '', from: 'fully_closed', notes: '' },
    { key: 'lsr', label: 'Rebound', clicks_total: '', from: 'fully_closed', notes: '' },
  ],
  pressure_min_psi: '',
  pressure_max_psi: '',
  tokens_min: '',
  tokens_max: '',
  sag_pct_min: '',
  sag_pct_max: '',
  source: 'workshop',
  verified: false,
  notes: '',
}

function numberOrNull(value) {
  if (value === '' || value === null || value === undefined) return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function intOrNull(value) {
  const parsed = numberOrNull(value)
  return parsed === null ? null : Math.round(parsed)
}

function toForm(spec) {
  if (!spec) return { ...EMPTY, adjusters: EMPTY.adjusters.map(a => ({ ...a })) }
  return {
    component_type: spec.component_type || 'fork',
    brand: spec.brand || '',
    model: spec.model || '',
    model_code: spec.model_code || '',
    year_from: spec.year_from ?? '',
    year_to: spec.year_to ?? '',
    damper: spec.damper || '',
    spring_type: spec.spring_type || 'unknown',
    travel_options: Array.isArray(spec.travel_options) ? spec.travel_options.join(', ') : '',
    adjusters: Array.isArray(spec.adjusters) && spec.adjusters.length ? spec.adjusters : [],
    pressure_min_psi: spec.pressure_min_psi ?? '',
    pressure_max_psi: spec.pressure_max_psi ?? '',
    tokens_min: spec.tokens_min ?? '',
    tokens_max: spec.tokens_max ?? '',
    sag_pct_min: spec.sag_pct_min ?? '',
    sag_pct_max: spec.sag_pct_max ?? '',
    source: spec.source || 'workshop',
    verified: !!spec.verified,
    notes: spec.notes || '',
  }
}

export default function ComponentSpecsAdmin({ workshopId }) {
  const [specs, setSpecs] = useState([])
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(() => toForm(null))
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  async function load() {
    if (!workshopId) return
    const { data, error: loadError } = await supabase
      .from('component_specs')
      .select('*')
      .eq('workshop_id', workshopId)
      .order('component_type')
      .order('brand')
      .order('model')
    if (!loadError) setSpecs(data || [])
    setLoading(false)
  }

  useEffect(() => { load() }, [workshopId])

  function setField(key, value) {
    setForm(f => ({ ...f, [key]: value }))
  }

  function setAdjuster(index, key, value) {
    setForm(f => ({
      ...f,
      adjusters: f.adjusters.map((row, rowIndex) => rowIndex === index ? { ...row, [key]: value } : row),
    }))
  }

  function startEdit(spec) {
    setEditing(spec)
    setForm(toForm(spec))
    setMessage('')
    setError('')
  }

  function startNew() {
    setEditing(null)
    setForm(toForm(null))
    setMessage('')
    setError('')
  }

  function payload() {
    const travelOptions = String(form.travel_options || '')
      .split(',')
      .map(v => intOrNull(v.trim()))
      .filter(v => v !== null)
    const adjusters = (form.adjusters || [])
      .map(row => ({
        key: String(row.key || '').trim().toLowerCase(),
        label: String(row.label || '').trim(),
        clicks_total: intOrNull(row.clicks_total),
        from: String(row.from || '').trim(),
        notes: String(row.notes || '').trim(),
      }))
      .filter(row => row.key)

    return {
      workshop_id: workshopId,
      component_type: form.component_type,
      brand: form.brand.trim(),
      model: form.model.trim(),
      model_code: form.model_code.trim() || null,
      year_from: intOrNull(form.year_from),
      year_to: intOrNull(form.year_to),
      damper: form.damper.trim() || null,
      spring_type: form.spring_type || 'unknown',
      travel_options: travelOptions.length ? travelOptions : null,
      adjusters: adjusters.length ? adjusters : null,
      pressure_min_psi: intOrNull(form.pressure_min_psi),
      pressure_max_psi: intOrNull(form.pressure_max_psi),
      tokens_min: intOrNull(form.tokens_min),
      tokens_max: intOrNull(form.tokens_max),
      sag_pct_min: numberOrNull(form.sag_pct_min),
      sag_pct_max: numberOrNull(form.sag_pct_max),
      source: form.source || 'workshop',
      verified: !!form.verified,
      notes: form.notes.trim() || null,
    }
  }

  async function save() {
    setSaving(true)
    setMessage('')
    setError('')
    const row = payload()
    if (!row.brand || !row.model || !row.component_type) {
      setError('Component type, brand and model are required.')
      setSaving(false)
      return
    }
    const result = editing?.id
      ? await supabase.from('component_specs').update(row).eq('id', editing.id)
      : await supabase.from('component_specs').insert(row)
    setSaving(false)
    if (result.error) {
      setError(result.error.message)
      return
    }
    startNew()
    setMessage('Component spec saved.')
    await load()
  }

  async function remove(spec) {
    if (!window.confirm(`Delete ${spec.brand} ${spec.model}?`)) return
    await supabase.from('component_specs').delete().eq('id', spec.id)
    await load()
  }

  if (loading) return <div className="text-muted fs12">Loading specs...</div>

  return (
    <div className="grid-2" style={{ alignItems: 'flex-start' }}>
      <div className="card" style={{ maxWidth: 720 }}>
        <div className="card-title">{editing ? 'Edit Component Spec' : 'Add Component Spec'}</div>
        <div className="text-muted fs12" style={{ marginBottom: 14 }}>
          Specs are workshop-private in this version. Leave unknown hardware facts blank; Rebound will not guess them.
        </div>
        {message && <div className="alert alert-success" style={{ marginBottom: 12 }}>{message}</div>}
        {error && <div className="alert alert-error" style={{ marginBottom: 12 }}>{error}</div>}
        <div className="grid-3">
          <div className="field"><label>Type</label><select value={form.component_type} onChange={e => setField('component_type', e.target.value)}><option value="fork">Fork</option><option value="shock">Shock</option><option value="dropper">Dropper</option></select></div>
          <div className="field"><label>Brand</label><input value={form.brand} onChange={e => setField('brand', e.target.value)} placeholder="RockShox, Fox..." /></div>
          <div className="field"><label>Model</label><input value={form.model} onChange={e => setField('model', e.target.value)} placeholder="ZEB Ultimate" /></div>
        </div>
        <div className="grid-3">
          <div className="field"><label>Model Code</label><input value={form.model_code} onChange={e => setField('model_code', e.target.value)} placeholder="FS-ZEB-ULT-A3" /></div>
          <div className="field"><label>Year From</label><input type="number" value={form.year_from} onChange={e => setField('year_from', e.target.value)} /></div>
          <div className="field"><label>Year To</label><input type="number" value={form.year_to} onChange={e => setField('year_to', e.target.value)} /></div>
        </div>
        <div className="grid-3">
          <div className="field"><label>Damper</label><input value={form.damper} onChange={e => setField('damper', e.target.value)} placeholder="Charger 3, GRIP2..." /></div>
          <div className="field"><label>Spring Type</label><select value={form.spring_type} onChange={e => setField('spring_type', e.target.value)}><option value="air">Air</option><option value="coil">Coil</option><option value="unknown">Unknown</option></select></div>
          <div className="field"><label>Travel Options</label><input value={form.travel_options} onChange={e => setField('travel_options', e.target.value)} placeholder="150, 160, 170" /></div>
        </div>
        <div style={{ background: 'var(--black)', border: '1px solid var(--border)', borderRadius: 6, padding: 12, marginBottom: 14 }}>
          <div className="card-title" style={{ marginBottom: 8 }}>Adjusters</div>
          {form.adjusters.map((row, index) => (
            <div key={index} className="grid-4" style={{ marginBottom: 8 }}>
              <input value={row.key || ''} onChange={e => setAdjuster(index, 'key', e.target.value)} placeholder="lsc" />
              <input value={row.label || ''} onChange={e => setAdjuster(index, 'label', e.target.value)} placeholder="Low Speed Compression" />
              <input type="number" value={row.clicks_total ?? ''} onChange={e => setAdjuster(index, 'clicks_total', e.target.value)} placeholder="Clicks total" />
              <input value={row.from || ''} onChange={e => setAdjuster(index, 'from', e.target.value)} placeholder="fully_closed" />
            </div>
          ))}
          <button className="btn btn-ghost btn-sm" onClick={() => setForm(f => ({ ...f, adjusters: [...f.adjusters, { key: '', label: '', clicks_total: '', from: 'fully_closed', notes: '' }] }))}>+ Add adjuster</button>
        </div>
        <div className="grid-3">
          <div className="field"><label>Pressure Min PSI</label><input type="number" value={form.pressure_min_psi} onChange={e => setField('pressure_min_psi', e.target.value)} /></div>
          <div className="field"><label>Pressure Max PSI</label><input type="number" value={form.pressure_max_psi} onChange={e => setField('pressure_max_psi', e.target.value)} /></div>
          <div className="field"><label>Source</label><select value={form.source} onChange={e => setField('source', e.target.value)}><option value="workshop">Workshop</option><option value="manufacturer">Manufacturer</option><option value="unknown">Unknown</option></select></div>
        </div>
        <div className="grid-4">
          <div className="field"><label>Tokens Min</label><input type="number" value={form.tokens_min} onChange={e => setField('tokens_min', e.target.value)} /></div>
          <div className="field"><label>Tokens Max</label><input type="number" value={form.tokens_max} onChange={e => setField('tokens_max', e.target.value)} /></div>
          <div className="field"><label>Sag Min %</label><input type="number" value={form.sag_pct_min} onChange={e => setField('sag_pct_min', e.target.value)} /></div>
          <div className="field"><label>Sag Max %</label><input type="number" value={form.sag_pct_max} onChange={e => setField('sag_pct_max', e.target.value)} /></div>
        </div>
        <label className="check-item" style={{ marginBottom: 12 }}>
          <input type="checkbox" checked={form.verified} onChange={e => setField('verified', e.target.checked)} />
          Verified by workshop
        </label>
        <div className="field"><label>Notes</label><textarea value={form.notes} onChange={e => setField('notes', e.target.value)} /></div>
        <div className="action-row">
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save Spec'}</button>
          {editing && <button className="btn btn-ghost" onClick={startNew}>Cancel Edit</button>}
        </div>
      </div>

      <div className="card" style={{ maxWidth: 720 }}>
        <div className="card-title">Workshop Specs</div>
        {!specs.length ? (
          <div className="text-muted fs12">No component specs yet. Add 20-30 common models to make the setup engine useful.</div>
        ) : specs.map(spec => (
          <div key={spec.id} style={{ padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <div className="text-white fw7">{spec.brand} {spec.model}</div>
                <div className="text-muted fs12">{spec.component_type} {spec.model_code ? `- ${spec.model_code}` : ''} {spec.year_from || spec.year_to ? `- ${spec.year_from || '?'}-${spec.year_to || '?'}` : ''}</div>
                <div style={{ marginTop: 5 }}>
                  <StatusPill tone={spec.verified ? 'green' : 'muted'}>{spec.verified ? 'Verified' : 'Unverified'}</StatusPill>
                </div>
              </div>
              <div className="action-row">
                <button className="btn btn-ghost btn-sm" onClick={() => startEdit(spec)}>Edit</button>
                <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)', borderColor: 'var(--red)' }} onClick={() => remove(spec)}>Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
